import { useEffect, useRef, useState, type CSSProperties } from "react";
import { testIds } from "@/testIds";
import { errorText, isOurSide, splitExcerpt, type FigureError } from "@/lib/tikzFigure";
import { lastSize, rememberSize, type TikzDrawing } from "./useTikzDrawing";
import styles from "./TikzFigure.module.scss";

const T = testIds.figureTikz;

/** Before a figure has ever drawn: a width and an aspect that suit most pictures. */
const DEFAULT_BOX = { w: 360, h: 240 };

/**
 * The paper panel. In Dark the drawing sits on the real paper colour (the
 * look's paper, else white): black TikZ lines would vanish on a dark page, and
 * recolouring them would break the author's own colours.
 */
export function PaperPanel({ dark, children, testId }: { dark: boolean; children: React.ReactNode; testId?: string }) {
  const style: CSSProperties | undefined = dark ? { background: "var(--lilia-look-true-paper, #FFFFFF)" } : undefined;
  return (
    <div className={styles.paper} data-paper-panel={dark ? "true" : undefined} data-testid={testId} style={style}>
      {children}
    </div>
  );
}

export function ourSideWording(kind: FigureError["kind"]): string {
  switch (kind) {
    case "timeout":
      return "Drawing it took longer than we allow. That's a limit on our side, not a mistake in your figure.";
    case "budget":
      return "Many figures were drawn in a short time, so this one has to wait. That's a limit on our side, not a mistake in your figure.";
    default:
      return "We couldn't reach the drawing service. That's on our side, not a mistake in your figure.";
  }
}

function ErrorBox({
  error,
  onShowSource,
  onRetry,
}: {
  error: FigureError;
  onShowSource: () => void;
  onRetry: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const ourSide = isOurSide(error.kind);
  const copy = () => {
    void navigator.clipboard?.writeText(errorText(error)).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };
  const [before, bad, after] = error.excerpt !== null ? splitExcerpt(error.excerpt, error.column) : ["", "", ""];
  return (
    <div className={styles.error} data-testid={T.error} data-kind={error.kind} role="status">
      <p className={styles.errorTitle}>This figure didn&apos;t draw.</p>
      {ourSide ? (
        <>
          <p className={styles.errorMessage}>{ourSideWording(error.kind)}</p>
          <div className={styles.actions}>
            <button type="button" data-testid={T.tryAgain} onClick={onRetry}>
              Try again
            </button>
          </div>
        </>
      ) : (
        <>
          <p className={styles.errorMessage}>{error.message}</p>
          {error.excerpt !== null && (
            <pre className={styles.excerpt} data-line={error.line ?? undefined}>
              {error.line !== null && <span className={styles.excerptLine}>{error.line}</span>}
              {before}
              <u data-bad="true">{bad}</u>
              {after}
            </pre>
          )}
          <div className={styles.actions}>
            <button type="button" data-testid={T.showSource} onClick={onShowSource}>
              Show source
            </button>
            <button type="button" data-testid={T.copyError} onClick={copy}>
              {copied ? "Copied" : "Copy error"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** The figure flipped to its code: read-only, numbered lines, Copy, and Edit (the split view). */
function CodeView({
  source,
  highlightLine,
  highlightTo,
  onEdit,
}: {
  source: string;
  highlightLine: number | null;
  /** The last highlighted line (an Ask Lilia line chip spans several). */
  highlightTo?: number | null;
  onEdit: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const lineRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    lineRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [highlightLine]);
  const lines = source.split("\n");
  return (
    <div className={styles.code} data-testid={T.code} contentEditable={false}>
      <div className={styles.codeHead}>
        <span>Source</span>
        <button type="button" data-testid={T.codeEdit} title="Edit in the split view (⌥⏎)" onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          data-testid={T.codeCopy}
          onClick={() => {
            void navigator.clipboard?.writeText(source).then(() => setCopied(true), () => setCopied(false));
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className={styles.codeBody}>
        {lines.map((text, i) => {
          const n = i + 1;
          const hit = highlightLine !== null && n >= highlightLine && n <= Math.max(highlightLine, highlightTo ?? highlightLine);
          return (
            <div
              key={n}
              ref={n === highlightLine ? lineRef : undefined}
              className={styles.codeLine}
              data-line={n}
              data-highlighted={hit ? "true" : undefined}
            >
              <span className={styles.lineNo} aria-hidden="true">
                {n}
              </span>
              <span className={styles.lineText}>{text || " "}</span>
            </div>
          );
        })}
      </pre>
    </div>
  );
}

export interface TikzFigureProps {
  blockId: string;
  source: string;
  drawing: TikzDrawing;
  /** No caption: a bare picture (⋯ offers "Make a numbered figure"). */
  bare: boolean;
  selected: boolean;
  dark: boolean;
  alt: string;
  view: "drawing" | "source";
  /** The source line to highlight in the code view (Show source). */
  highlightLine: number | null;
  /** With highlightLine, the last line of a range (an Ask Lilia line chip). */
  highlightTo?: number | null;
  onToggleSource: () => void;
  onShowSource: (line: number | null) => void;
  /** The split view (Source → Edit, ⋯ → Edit source, ⌥⏎ twice). */
  onEditSource: () => void;
  /** Placement and alt text, in the edit drawer. */
  onDetails: () => void;
  onMakeNumbered: () => void;
  /** ⋯ → Ask Lilia: Change… / Explain this figure (1c): the dock, scoped to this figure. */
  onAsk?: (intent: "change" | "explain") => void;
  /** A plot linked to a table (step 4a, 1c): its chip replaces "TikZ", Plot settings replaces Source. */
  plot?: {
    /** "Table 2", "a table". */
    table: string;
    state: "linked" | "removed" | "problem";
    /** The table just changed and the plot was written again ("Updated just now", 4 s). */
    updated: boolean;
    onSettings: () => void;
    onSelectTable: () => void;
    onDetach: () => void;
  };
}

/**
 * A TikZ figure on the canvas: the drawing on the paper, like any figure. The
 * bar (on hover or selection) reads TikZ · Source · ⋯. Loading holds the
 * figure's space; errors stay inside the figure's box.
 */
export function TikzFigure(props: TikzFigureProps) {
  const { blockId, source, drawing, bare, selected, dark, alt, view, highlightLine } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const { svg, loading, slow, error, lastGood, retry } = drawing;
  const recompiling = loading && !!svg;

  const state = !source.trim()
    ? "empty"
    : error && !loading
      ? "error"
      : svg
        ? loading
          ? "drawing"
          : "drawn"
        : "first";

  const box = lastSize(blockId) ?? DEFAULT_BOX;

  const body = () => {
    if (state === "empty") {
      return (
        <div className={styles.firstBox} style={{ width: DEFAULT_BOX.w, aspectRatio: `${DEFAULT_BOX.w} / ${DEFAULT_BOX.h}` }}>
          This figure has no TikZ source yet.
        </div>
      );
    }
    if (state === "error" && error) {
      return (
        <>
          <ErrorBox error={error} onShowSource={() => props.onShowSource(error.line)} onRetry={retry} />
          {lastGood && (
            <div className={styles.lastGood} data-testid={T.lastGood}>
              <span className={styles.lastGoodLabel}>Last version that drew</span>
              <PaperPanel dark={dark}>
                <img src={lastGood} alt={alt} className={styles.drawing} data-dim="true" />
              </PaperPanel>
            </div>
          )}
        </>
      );
    }
    if (svg) {
      return (
        <PaperPanel dark={dark}>
          <img
            src={svg}
            alt={alt}
            className={styles.drawing}
            data-testid={T.drawing}
            data-dim={recompiling ? "true" : undefined}
            onLoad={(e) => rememberSize(blockId, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
          />
        </PaperPanel>
      );
    }
    // The first compile: a dashed box holding the space, a spinner, and after
    // a few seconds the note that the first time is slow.
    return (
      <div
        className={styles.firstBox}
        data-testid={T.firstLoad}
        data-slow={slow ? "true" : undefined}
        style={{ width: box.w, aspectRatio: `${box.w} / ${box.h}` }}
        role="status"
        aria-live="polite"
      >
        <div className={styles.spinner} aria-hidden="true" />
        <span>Drawing…</span>
        {slow && <span>The first drawing takes a little longer. The next ones are quicker.</span>}
      </div>
    );
  };

  return (
    <div
      className={styles.tikz}
      data-testid={T.root}
      data-block-id={blockId}
      data-state={state}
      data-view={view}
      data-bare={bare ? "true" : undefined}
      data-selected={selected ? "true" : undefined}
    >
      <div
        className={styles.bar}
        data-testid={T.bar}
        data-drawing={recompiling ? "true" : undefined}
        data-menu-open={menuOpen ? "true" : undefined}
        contentEditable={false}
        // The menu closes when focus leaves the bar.
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setMenuOpen(false);
        }}
      >
        {props.plot ? (
          <>
            <button
              type="button"
              className={styles.plotChip}
              data-testid={testIds.plot.linkChip}
              data-state={props.plot.state}
              title={props.plot.state === "removed" ? "The table was removed: the plot no longer updates" : `Select ${props.plot.table}`}
              onClick={props.plot.state === "removed" ? undefined : props.plot.onSelectTable}
            >
              {props.plot.state === "removed" ? "Plot · Table removed" : `Plot · ${capitalise(props.plot.table)}`}
            </button>
            {props.plot.state === "removed" ? (
              <>
                <span className={styles.barNote}>no longer updates</span>
                <button type="button" onClick={props.plot.onDetach}>
                  Detach
                </button>
              </>
            ) : (
              <>
                {(recompiling || props.plot.updated) && <span className={styles.barNote}>{recompiling ? "Drawing…" : "Updated just now"}</span>}
                <button type="button" data-testid={testIds.plot.settingsOpen} onClick={props.plot.onSettings}>
                  Plot settings
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <span className={styles.barLabel}>{recompiling ? "Drawing…" : "TikZ"}</span>
            <span className={styles.barDot} aria-hidden="true">
              ·
            </span>
            <button
              type="button"
              data-testid={T.sourceToggle}
              aria-pressed={view === "source"}
              title="Show the TikZ source (⌥⏎)"
              onClick={props.onToggleSource}
            >
              Source
            </button>
          </>
        )}
        <span className={styles.barDot} aria-hidden="true">
          ·
        </span>
        <button
          type="button"
          data-testid={T.more}
          aria-label="More for this figure"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          ⋯
        </button>
        {menuOpen && (
          <div className={styles.menu} role="menu" onKeyDown={(e) => e.key === "Escape" && setMenuOpen(false)}>
            {props.plot && (
              <button
                type="button"
                role="menuitem"
                data-testid={T.sourceToggle}
                aria-pressed={view === "source"}
                onClick={() => {
                  setMenuOpen(false);
                  props.onToggleSource();
                }}
              >
                {view === "source" ? "Show the plot" : "Show source"}
                <span className={styles.menuKey}>⌥⏎</span>
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              data-testid={T.editSource}
              onClick={() => {
                setMenuOpen(false);
                props.onEditSource();
              }}
            >
              Edit source
              <span className={styles.menuKey}>⌥⏎ ⌥⏎</span>
            </button>
            <button
              type="button"
              role="menuitem"
              data-testid={T.details}
              onClick={() => {
                setMenuOpen(false);
                props.onDetails();
              }}
            >
              Figure details…
            </button>
            {props.onAsk && (
              <>
                <span className={styles.menuSep} role="separator" />
                <button
                  type="button"
                  role="menuitem"
                  data-testid={T.askChange}
                  data-ask="true"
                  onClick={() => {
                    setMenuOpen(false);
                    props.onAsk?.("change");
                  }}
                >
                  Ask Lilia: Change…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  data-testid={T.askExplain}
                  data-ask="true"
                  onClick={() => {
                    setMenuOpen(false);
                    props.onAsk?.("explain");
                  }}
                >
                  Ask Lilia: Explain this figure
                </button>
              </>
            )}
            {bare && (
              <button
                type="button"
                role="menuitem"
                data-testid={T.makeNumbered}
                onClick={() => {
                  setMenuOpen(false);
                  props.onMakeNumbered();
                }}
              >
                Make a numbered figure
              </button>
            )}
          </div>
        )}
      </div>
      {view === "source" ? (
        <CodeView source={source} highlightLine={highlightLine} highlightTo={props.highlightTo} onEdit={props.onEditSource} />
      ) : (
        body()
      )}
    </div>
  );
}

function capitalise(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
